
import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface UploadButtonProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  children?: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  asChild?: boolean;
}

const UploadButton = React.forwardRef<HTMLInputElement, UploadButtonProps>(
  ({ className, variant = "default", size = "default", asChild = false, children, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const composedRef = React.useCallback(
      (element: HTMLInputElement | null) => {
        if (ref) {
          if (typeof ref === "function") {
            ref(element);
          } else {
            ref.current = element;
          }
        }
        if (inputRef.current) {
          inputRef.current = element;
        }
      },
      [ref]
    );

    return (
      <div className={cn("relative", className)}>
        <Input
          type="file"
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          ref={composedRef}
          {...props}
        />
        <Button type="button" variant={variant} size={size} asChild={asChild}>
          {children || "Upload"}
        </Button>
      </div>
    );
  }
);

UploadButton.displayName = "UploadButton";

export { UploadButton };
